<template>
  <div class="import_config_wrapper">
    <div class="mgx_action_btn" @click="triggerFileInput">
      <div class="icon-wrapper">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3V16M12 16L16 12M12 16L8 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M20 17V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <span>{{ $t('import_config') }}</span>
    </div>
    <input id="fileInput" type="file" accept="*" class="input_file" @change="importConfig" />
  </div>
</template>

<script setup lang="ts">
import { configSet } from '@/service/driver/config_set'
import { DialogType, enableDialog } from '../cdialog/dialog'
import i18n from '@/languages/i18n'
import { DeviceManager } from '@/service/device'
import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { travelConfig } from '@/define/magnet_travel'
import { DeviceCapability } from '@/service/device_capability'
import { DataProfile } from '@hid/snapshot/profile'
import { MagnetSeniorType } from '@libs/types/enum'
import { DataTravel } from '@libs/types/response'
import { KK, SetMagnetSenior } from '@hid/define/define'
import { importConfigTravelClampValue } from '@/service/driver/travel_converter'
let { t } = i18n.global

const triggerFileInput = () => {
  const fileInput = document.getElementById('fileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}
// 同厂商的设备配置文件可以互相导入
const importConfig = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const file = target.files[0]
    const reader = new FileReader()
    reader.onload = async (e) => {
      const result = e.target?.result
      if (typeof result === 'string') {
        const data: { name: string; id: number; content: string } = JSON.parse(result)
        if (!checkId(data.id)) {
          enableDialog(t('file_id_error'), DialogType.notice, false)
        } else {
          const content = await processImportedData(data.content)
          configSet.setConfigPreset(data.name, content)
        }
        target.value = ''
      }
    }
    reader.onerror = () => {
      enableDialog(t('file_read_error'), DialogType.notice, false, 2000)
    }
    reader.readAsText(file, 'UTF-8')
  }
}

// 相同设备商的设备支持互相导入
const checkId = (importID: number): boolean => {
  if (
    importID == DeviceManager.instance.state.currentPid ||
    (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2) && specialCheck(speciaBehiver.DevicesOfJingtai_V2, importID)) ||
    (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1) && specialCheck(speciaBehiver.DevicesOfJingtai_V1, importID)) ||
    (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) && specialCheck(speciaBehiver.DeviceOfRoyuan, importID)) ||
    (specialCheckCurrent(speciaBehiver.DevicesOfWitmod) && specialCheck(speciaBehiver.DevicesOfWitmod, importID)) ||
    (specialCheckCurrent(speciaBehiver.DeviceHe3SE) && specialCheck(speciaBehiver.DeviceHe3SE, importID)) ||
    (specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen) && specialCheck(speciaBehiver.DevicesOfHuoChaiRen, importID))
  ) {
    return true
  }
  return false
}

const processImportedData = async (encodedData: string): Promise<string> => {
  const decodedString = atob(encodedData)
  const parsedProfile: DataProfile = JSON.parse(decodedString)

  // 数据处理
  const clampedProfile = clampMagnetTravelValues(parsedProfile)
  const checkedProfile = await checkFeatureSupport(clampedProfile)

  // 编码返回
  return btoa(JSON.stringify(checkedProfile))
}

// 判断设备是否支持KME, RS， 音律律动等功能
// KME 、RS 功能不支持，则不设置
// 音律律动功能不支持，则改成默认灯效
const checkFeatureSupport = async (originalData: DataProfile): Promise<DataProfile> => {
  const processedProfile: DataProfile = JSON.parse(JSON.stringify(originalData))
  if (processedProfile.ColorsDynamic.Run) {
    const isMusicRhythmAvailable = await DeviceCapability.isMusicRhythmAvailable()
    if (!isMusicRhythmAvailable) {
      processedProfile.ColorsDynamic.Run = false
      processedProfile.LightMode.Mode = 1
    }
  }
  // 并行检测设备能力
  const [isRS, isKME] = await Promise.all([DeviceCapability.isRSSupported(), DeviceCapability.isSupportKME()])
  const safeMap: Map<KK, SetMagnetSenior> = processedProfile.MagnetData ? new Map(Object.entries(processedProfile.MagnetData)) : new Map()

  const filteredMap = new Map<number, SetMagnetSenior>()
  for (const [key, item] of safeMap) {
    const isSupportRS = item.KeyType === MagnetSeniorType.SeniorKeyTypeRS
    const isSupportKME = item.KeyType === MagnetSeniorType.SeniorKeyTypeKME
    if (isSupportRS || isSupportKME) {
      if (isRS || isKME) {
        filteredMap.set(key, item)
      }
    } else {
      filteredMap.set(key, item)
    }
  }
  processedProfile.MagnetData = filteredMap
  return processedProfile
}

// 睿连维厂商的设备的行程范围不一致,在导入时需要做特殊处理下
const clampMagnetTravelValues = (profile: DataProfile): DataProfile => {
  const deviceConfig = travelConfig(DeviceManager.instance.state.currentPid)
  const { maxTravel, step } = deviceConfig
  const minValue = Math.floor(maxTravel / step) // 明确取整逻辑

  // 处理行程数据
  if (profile.MagnetTravel) {
    const travelMap = new Map(Object.entries(profile.MagnetTravel))
    const clampedMap = new Map<KK, DataTravel>()

    for (const [key, data] of travelMap) {
      const clampedData: DataTravel = {
        ...data,
        PressNum: clampValue(data.PressNum, minValue, maxTravel),
        ReleaseNum: clampValue(data.ReleaseNum, minValue, maxTravel),
        QuickTriggerPressNum: data.QuickTriggerPressNum !== 0 ? clampValue(data.QuickTriggerPressNum, minValue, maxTravel) : 0,
        QuickTriggerReleaseNum: data.QuickTriggerReleaseNum !== 0 ? clampValue(data.QuickTriggerReleaseNum, minValue, maxTravel) : 0,
      }

      clampedMap.set(Number(key) as KK, clampedData) // 创建新对象避免副作用
    }

    profile.MagnetTravel = clampedMap
  }

  return profile
}
// 通用值域限制方法
const clampValue = (value: number, min: number, max: number): number => {
  return importConfigTravelClampValue(value, min, max)
}
</script>

<style scoped lang="less">
.input_file {
  display: none;
}
</style>
